import { createAsyncThunk } from '@reduxjs/toolkit'
import AxiosClient from '../AxiosClient'
import { OMS_BASE } from 'src/utils/constants'

/**
 * Create a new OMS order shipment
 * POST /oms/order-shipment-fulfilled
 */
export const createOmsShipmentFulfilledAsync = createAsyncThunk(
  'omsShipping/createOmsShipmentFulfilledAsync',
  async function (payload: any, toolkit) {
    const url = `${OMS_BASE}/order-shipment-fulfilled`
    return await AxiosClient('POST', url, payload, toolkit, [])
  }
)

/**
 * Update an existing OMS order shipment
 * PUT /oms/order-shipment-fulfilled/:shipment_id
 */
export const updateOmsShipmentFulfilledAsync = createAsyncThunk(
  'omsShipping/updateOmsShipmentFulfilledAsync',
  async function (payload: { id: number; data: any }, toolkit) {
    const url = `${OMS_BASE}/order-shipment-fulfilled/${payload.id}`
    return await AxiosClient('PUT', url, payload.data, toolkit, [])
  }
)

/**
 * Update the status of an existing OMS order shipment
 * PUT /oms/order-shipments/:shipment_id?is_status_update=true
 */
/**
 * Update the status of an existing OMS order shipment
 * PUT /oms/order-shipments/:shipment_id?is_status_update=true
 */
export const updateOmsShipmentStatusAsync = createAsyncThunk(
  'omsShipping/updateOmsShipmentStatusAsync',
  async function (payload: { id: number; shipment_status: string } & any, toolkit) {
    const { id, ...data } = payload
    const url = `${OMS_BASE}/order-shipment-fulfilled/${id}?is_status_update=true`
    return await AxiosClient('PUT', url, data, toolkit, [])
  }
)

/**
 * Delete an existing OMS order shipment
 * DELETE /oms/order-shipments/:shipment_id
 */
export const deleteOmsShipmentAsync = createAsyncThunk(
  'omsShipping/deleteOmsShipmentAsync',
  async function (payload: { id: number }, toolkit) {
    const url = `${OMS_BASE}/order-shipments/${payload.id}`
    return await AxiosClient('DELETE', url, [], toolkit, [])
  }
)

/**
 * Fetch OMS order shipments (all-rounder endpoint with filters)
 * GET /oms/order-shipments?id=:shipmentId&...
 * For fetching a single shipment by ID, just pass { id: shipmentId }
 */
export const getOmsShipmentListAsync = createAsyncThunk(
  'omsShipping/getOmsShipmentListAsync',
  async function (
    params: {
      id?: number
      order_id?: string
      search?: string
      page?: number
      limit?: number
      order_by?: string
      sort_by?: string
      is_active?: boolean
      sync_status?: string
      shipment_status?: string
    },
    toolkit
  ) {
    const query = new URLSearchParams()
    if (params.id != null) query.append('id', String(params.id))
    if (params.order_id) query.append('order_id', params.order_id)
    if (params.search) query.append('search', params.search)
    if (params.page != null) query.append('page', String(params.page))
    if (params.limit != null) query.append('limit', String(params.limit))
    if (params.order_by) query.append('order_by', params.order_by)
    if (params.sort_by) query.append('sort_by', params.sort_by)
    if (params.is_active !== undefined) query.append('is_active', String(params.is_active))
    if (params.sync_status) query.append('sync_status', params.sync_status)
    if (params.shipment_status) query.append('shipment_status', params.shipment_status)

    const url = `${OMS_BASE}/order-shipments?${query.toString()}`
    return await AxiosClient('GET', url, [], toolkit, [], toolkit.signal)
  }
)

/**
 * Resolve next_url from paginated API response to a relative path
 * Strips REACT_APP_API_URL prefix or /market_connect prefix from absolute URLs
 */
const resolveNextUrl = (nextUrl: string): string => {
  const apiBase = process.env.REACT_APP_API_URL || ''

  if (nextUrl.startsWith(apiBase)) {
    return nextUrl.slice(apiBase.length)
  }

  try {
    const parsed = new URL(nextUrl)
    const path = parsed.pathname.replace(/^\/market_connect/, '')
    return `${path}${parsed.search}`
  } catch {
    return nextUrl
  }
}

/**
 * Fetch the SHIPPINGPROVIDER platform field for the OMS platform
 * GET /api/platform-fields/{platformId}?field_type=NATIVE&includeLocalFields=false&owner=ORDER
 * Returns the platform field array — caller extracts the field with api_code === 'SHIPPINGPROVIDER'
 */
export const getOmsShippingCarrierFieldAsync = createAsyncThunk(
  'omsShipping/getOmsShippingCarrierFieldAsync',
  async function (params: { platformId: number }, toolkit) {
    return await AxiosClient(
      'GET',
      `/api/platform-fields/${params.platformId}`,
      [],
      toolkit,
      { field_type: 'NATIVE', includeLocalFields: false, owner: 'SHIPMENT' }
    )
  }
)

/**
 * Fetch paginated carrier options from platform-field-data
 * GET /api/platform-field-data/{fieldId}?use_pagination=true&search_string=...
 * Supports pagination via next_url and search via search_string
 */
export const getOmsShippingCarrierOptionsAsync = createAsyncThunk(
  'omsShipping/getOmsShippingCarrierOptionsAsync',
  async function (
    params: {
      fieldId?: number
      search_string?: string
      next_url?: string
      signal?: AbortSignal
    },
    toolkit
  ) {
    if (params.next_url) {
      return await AxiosClient(
        'GET',
        resolveNextUrl(params.next_url),
        [],
        toolkit,
        {},
        params.signal
      )
    }

    return await AxiosClient(
      'GET',
      `/api/platform-field-data/${params.fieldId}`,
      [],
      toolkit,
      { use_pagination: true, search_string: params.search_string ?? '' },
      params.signal
    )
  }
)
